import AlertCircleIcon from "lucide-react/dist/esm/icons/alert-circle";
import CheckCircle2Icon from "lucide-react/dist/esm/icons/check-circle-2";
import PlusIcon from "lucide-react/dist/esm/icons/plus";
import ActivityIcon from "lucide-react/dist/esm/icons/activity";
import AirplayIcon from "lucide-react/dist/esm/icons/airplay";
import AlertTriangleIcon from "lucide-react/dist/esm/icons/alert-triangle";
import AlignCenterIcon from "lucide-react/dist/esm/icons/align-center";
import AlignLeftIcon from "lucide-react/dist/esm/icons/align-left";
import AlignRightIcon from "lucide-react/dist/esm/icons/align-right";
import AnchorIcon from "lucide-react/dist/esm/icons/anchor";
import ApertureIcon from "lucide-react/dist/esm/icons/aperture";
import ArchiveIcon from "lucide-react/dist/esm/icons/archive";
import ArrowDownIcon from "lucide-react/dist/esm/icons/arrow-down";
import ArrowLeftIcon from "lucide-react/dist/esm/icons/arrow-left";
import ArrowRightIcon from "lucide-react/dist/esm/icons/arrow-right";
import ArrowUpIcon from "lucide-react/dist/esm/icons/arrow-up";
import AtSignIcon from "lucide-react/dist/esm/icons/at-sign";
import GripVerticalIcon from "lucide-react/dist/esm/icons/grip-vertical";
import AwardIcon from "lucide-react/dist/esm/icons/award";
import BarChartIcon from "lucide-react/dist/esm/icons/bar-chart";
import BatteryIcon from "lucide-react/dist/esm/icons/battery";
import BatteryChargingIcon from "lucide-react/dist/esm/icons/battery-charging";
import BellIcon from "lucide-react/dist/esm/icons/bell";
import BellOffIcon from "lucide-react/dist/esm/icons/bell-off";
import BluetoothIcon from "lucide-react/dist/esm/icons/bluetooth";
import BoldIcon from "lucide-react/dist/esm/icons/bold";
import BookIcon from "lucide-react/dist/esm/icons/book";
import BookmarkIcon from "lucide-react/dist/esm/icons/bookmark";
import BoxIcon from "lucide-react/dist/esm/icons/box";
import BriefcaseIcon from "lucide-react/dist/esm/icons/briefcase";
import CalendarIcon from "lucide-react/dist/esm/icons/calendar";
import CameraIcon from "lucide-react/dist/esm/icons/camera";
import CheckIcon from "lucide-react/dist/esm/icons/check";
import Trash2 from "lucide-react/dist/esm/icons/trash-2";
import EditIcon from "lucide-react/dist/esm/icons/edit";

import { toast } from "@quake/shared-ui/lib/sonner";
import { trpc } from "~/utils/trpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { schema as dataSchema } from "@quake/isomorphic/schemas/data.internal.schema";
import { isAttributeTypeSearchable } from "@quake/isomorphic/dataFilters";
import { makeAttributeId } from "@quake/isomorphic";
import { createFormHook, createFormHookContexts } from "@tanstack/react-form";
import { z } from "zod";
import { Input } from "@quake/shared-ui/components/ui/input";
import { Button } from "@quake/shared-ui/components/ui/button";
import { Label } from "@radix-ui/react-label";
import { TextEditor } from "@quake/shared-ui/components/ui/texteditor";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@quake/shared-ui/components/ui/card";
import { Alert, AlertDescription } from "@quake/shared-ui/components/ui/alert";
import { cn } from "@quake/shared-ui/lib/utils";
import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@quake/shared-ui/components/ui/select";
import { allowedTables } from "@quake/isomorphic/constants";
import { useAllowedTablesWithCustomEntities } from "~/hooks/useAllowedTablesWithCustomEntities";
import { Switch } from "@quake/shared-ui/components/ui/switch";
import { FancyMultiSelect } from "@quake/shared-ui/components/ui/fancy-multi-select-v2";
import { Textarea } from "@quake/shared-ui/components/ui/textarea";
import { inferOutput } from "@trpc/tanstack-react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@quake/shared-ui/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@quake/shared-ui/components/ui/tooltip";
import { useNavigate } from "@tanstack/react-router";
import { useParams } from "@tanstack/react-router";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";

const schema = dataSchema.extend({ title_attribute_ref: z.string().optional() });
const { fieldContext, formContext } = createFormHookContexts();

const entityTypes: [
  "Text",
  "Number",
  "Checkbox",
  "Date",
  "TimeStamp",
  "Select",
  "Multi Select",
  "Record",
  "Multi Record",
] = ["Text", "Number", "Checkbox", "Date", "TimeStamp", "Select", "Multi Select", "Record", "Multi Record"];
const optionsList = ["Select", "Multi Select"];
export const dateOptions = [
  { label: "1 month before", value: "1_month_before" },
  { label: "1 week before", value: "1_week_before" },
  { label: "1 day before", value: "1_day_before" },
  { label: "same day", value: "same_day" },
  { label: "1 day after", value: "1_day_after" },
  { label: "1 week after", value: "1_week_after" },
  { label: "1 month after", value: "1_month_after" },
];

export const lucideIcons = [
  { label: "Activity", value: "Activity", Icon: ActivityIcon },
  { label: "Airplay", value: "Airplay", Icon: AirplayIcon },
  { label: "Alert Circle", value: "AlertCircle", Icon: AlertCircleIcon },
  { label: "Alert Triangle", value: "AlertTriangle", Icon: AlertTriangleIcon },
  { label: "Align Center", value: "AlignCenter", Icon: AlignCenterIcon },
  { label: "Align Left", value: "AlignLeft", Icon: AlignLeftIcon },
  { label: "Align Right", value: "AlignRight", Icon: AlignRightIcon },
  { label: "Anchor", value: "Anchor", Icon: AnchorIcon },
  { label: "Aperture", value: "Aperture", Icon: ApertureIcon },
  { label: "Archive", value: "Archive", Icon: ArchiveIcon },
  { label: "Arrow Down", value: "ArrowDown", Icon: ArrowDownIcon },
  { label: "Arrow Left", value: "ArrowLeft", Icon: ArrowLeftIcon },
  { label: "Arrow Right", value: "ArrowRight", Icon: ArrowRightIcon },
  { label: "Arrow Up", value: "ArrowUp", Icon: ArrowUpIcon },
  { label: "At Sign", value: "AtSign", Icon: AtSignIcon },
  { label: "Award", value: "Award", Icon: AwardIcon },
  { label: "Bar Chart", value: "BarChart", Icon: BarChartIcon },
  { label: "Battery", value: "Battery", Icon: BatteryIcon },
  { label: "Battery Charging", value: "BatteryCharging", Icon: BatteryChargingIcon },
  { label: "Bell", value: "Bell", Icon: BellIcon },
  { label: "Bell Off", value: "BellOff", Icon: BellOffIcon },
  { label: "Bluetooth", value: "Bluetooth", Icon: BluetoothIcon },
  { label: "Bold", value: "Bold", Icon: BoldIcon },
  { label: "Book", value: "Book", Icon: BookIcon },
  { label: "Bookmark", value: "Bookmark", Icon: BookmarkIcon },
  { label: "Box", value: "Box", Icon: BoxIcon },
  { label: "Briefcase", value: "Briefcase", Icon: BriefcaseIcon },
  { label: "Calendar", value: "Calendar", Icon: CalendarIcon },
  { label: "Camera", value: "Camera", Icon: CameraIcon },
  { label: "Check", value: "Check", Icon: CheckIcon },
];

const attr = {
  id: "",
  name: "",
  description: "",
  searchable: false,
  type: "Text" as const,
  table_reference: "",
  options: [],
  defaultValue: "",
  boundaries: { required: false, unique: false },
  richText: false,
};

export function DataFormPage({
  entity,
  hideHeader = false,
  customTitle,
  customDescription,
}: {
  entity?: inferOutput<typeof trpc.customEntities.getCustomEntitiesById>;
  hideHeader?: boolean;
  customTitle?: string;
  customDescription?: string;
}) {
  const { data: session, isLoading: isSessionLoading } = useQuery(trpc.auth.getSession.queryOptions());
  const navigate = useNavigate();
  const { datasetSlug } = useParams({ from: "/_dashboard/$datasetSlug" });
  const [tempAttribute, setTempAttribute] = useState<NonNullable<z.infer<typeof schema>["attributes"]>[number]>(attr);
  const [tempOption, setTempOption] = useState<string>("");
  const [attrIndex, setAttrIndex] = useState<number>(-1);
  const [attributeDialogOpen, setAttributeDialogOpen] = useState(false);
  const [deleteWarningDialog, setDeleteWarningDialog] = useState<{
    open: boolean;
    index: number;
    warnings: string[];
  }>({ open: false, index: -1, warnings: [] });
  const queryClient = useQueryClient();
  const canCreateAttributes = !entity || session?.user?.role === "Administrator";

  const { data: customViews } = useQuery(
    trpc.customEntities.getCustomViews.queryOptions({ id: entity?.id || "" }, { enabled: !!entity?.id }),
  );

  // Fetch combined list of allowed tables including custom entities
  const { allAllowedTables } = useAllowedTablesWithCustomEntities(entity?.id);

  const createData = useMutation(
    trpc.customEntities.create.mutationOptions({
      onSuccess: async (res) => {
        toast.success("Created successfully");
        if (res) {
          await queryClient.invalidateQueries({
            queryKey: trpc.customEntities.getCustomModels.queryKey(),
          });
          navigate({ to: `/${datasetSlug}/data/custom/details/${res.id}/table` });
        }
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );
  const editData = useMutation(
    trpc.customEntities.update.mutationOptions({
      onSuccess: async (res) => {
        toast.success("Updated successfully");
        if (res) {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: trpc.customEntities.getCustomModels.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.customEntities.getCustomEntitiesById.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.customEntities.getCustomViews.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.customEntities.getCustomViewByName.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.customEntities.filters.queryKey(),
            }),
          ]);
          navigate({ to: `/${datasetSlug}/data/custom/details/${res}/table` });
        }
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  const { useAppForm } = createFormHook({
    fieldComponents: {
      Input,
      Textarea,
    },
    formComponents: {
      Button,
    },
    fieldContext,
    formContext,
  });

  const defaultValues: z.input<typeof schema> = {
    name: entity ? entity.name : "",
    p_name: entity ? entity.p_name : "",
    icon: entity?.icon ?? "",
    title_attribute_ref: entity ? entity.title_attribute_ref : "",
    attributes: entity
      ? entity.attributes.map((attribute) => ({
          ...attribute,
          boundaries: attribute.boundaries ?? { required: false, unique: false },
        }))
      : [],
  };

  const form = useAppForm({
    validators: {
      onChange: schema,
    },
    defaultValues,
    onSubmit: ({ value }) => {
      const data = schema.parse(value);
      if (entity) {
        editData.mutate({ ...data, id: entity.id });
        return;
      }
      createData.mutate(data);
    },
  });

  const addOption = () => {
    if (tempOption.trim() !== "") {
      if (tempAttribute?.options?.find((item) => item.value === tempOption)) {
        return toast.error("Option already created");
      }
      setTempAttribute((prev) => {
        return {
          ...prev,
          options: [
            ...(prev.options || []),
            { value: tempOption, label: tempOption, index: (prev.options || []).length },
          ],
        };
      });
      setTempOption("");
    }
  };

  const addAttribute = (field: any) => {
    if (tempAttribute.name.trim() === "" || tempAttribute.type.trim() === "") {
      return toast.error("Please fill the name and select a type");
    }

    const finalId = tempAttribute.id?.trim() || makeAttributeId(tempAttribute.name);
    if (!/^[a-z]+[a-z0-9_]*$/.test(finalId)) {
      return toast.error("ID must start with a letter and contain only lowercase letters, numbers, or underscores");
    }

    const isDuplicate = (field.state.value as Array<{ name: string; id?: string }>).some((item, index: number) => {
      if (index === attrIndex) return false;
      return item.name.trim() === tempAttribute.name.trim() || item.id === finalId;
    });

    if (isDuplicate) {
      return toast.error("Please select a unique name and ID");
    }
    if (tempAttribute.type === "Record" && !tempAttribute.table_reference) {
      return toast.error("Please select a record table");
    }
    if (optionsList.includes(tempAttribute.type) && tempAttribute.options?.length === 0) {
      return toast.error("Please add an option");
    }
    let tempA = [...field.state.value];

    if (attrIndex !== -1) {
      // Update in place to preserve order
      tempA[attrIndex] = { ...tempAttribute, id: tempAttribute.id || finalId };
    } else {
      tempA = [...tempA, { ...tempAttribute, id: finalId }];
    }
    field.setValue(tempA);
    setTempAttribute({ ...attr });
    setAttrIndex(-1);
    setTempOption("");
    setAttributeDialogOpen(false);
  };

  const btnTitle = () => {
    if (!entity) {
      if (createData.isPending) {
        return "Submitting...";
      }
      return "Save";
    }
    if (editData.isPending) {
      return "Updating...";
    }
    return "Save";
  };

  return (
    <div className="m-5">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <Card className="max-w-2xl mx-auto">
          {!hideHeader && (
            <CardHeader>
              <CardTitle>{customTitle || (!entity ? "New" : "Edit") + " Entity"}</CardTitle>
              {customDescription && <CardDescription>{customDescription}</CardDescription>}
              {!customDescription && <CardDescription>{!entity ? "Create a new" : "Edit"} entity</CardDescription>}
            </CardHeader>
          )}
          <CardContent className="space-y-4">
            <form.AppField
              name="name"
              children={(field) => (
                <>
                  <div className="flex items-center justify-between">
                    <Label
                      className={cn(
                        !schema.shape[field.name as keyof typeof schema.shape].isOptional() &&
                          "after:content-['*'] after:ml-0.5 after:text-red-500",
                      )}
                    >
                      Name
                    </Label>
                    {field.state.meta.errors ? (
                      <p className="text-xs font-medium text-destructive flex items-center">
                        <AlertCircleIcon className="h-3 w-3 mr-1" />
                        {field.state.meta.errors[0]?.message}
                      </p>
                    ) : null}
                  </div>
                  <field.Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </>
              )}
            />
            <form.AppField
              name="p_name"
              children={(field) => (
                <>
                  <div className="flex items-center justify-between">
                    <Label
                      className={cn(
                        !schema.shape[field.name as keyof typeof schema.shape].isOptional() &&
                          "after:content-['*'] after:ml-0.5 after:text-red-500",
                      )}
                    >
                      Plural Name:
                    </Label>
                    {field.state.meta.errors ? (
                      <p className="text-xs font-medium text-destructive flex items-center">
                        <AlertCircleIcon className="h-3 w-3 mr-1" />
                        {field.state.meta.errors[0]?.message}
                      </p>
                    ) : null}
                  </div>
                  <field.Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </>
              )}
            />
            <form.AppField
              name="icon"
              children={(field) => (
                <>
                  <div className="flex items-center justify-between">
                    <Label
                      className={cn(
                        !schema.shape[field.name as keyof typeof schema.shape].isOptional() &&
                          "after:content-['*'] after:ml-0.5 after:text-red-500",
                      )}
                    >
                      Icon:
                    </Label>
                    {field.state.meta.errors ? (
                      <p className="text-xs font-medium text-destructive flex items-center">
                        <AlertCircleIcon className="h-3 w-3 mr-1" />
                        {field.state.meta.errors[0]?.message}
                      </p>
                    ) : null}
                  </div>
                  <Select value={field.state.value} onValueChange={(e) => field.handleChange(e)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select Option" />
                    </SelectTrigger>
                    <SelectContent>
                      {lucideIcons.map((opt) => {
                        let IconComponent = opt.Icon;
                        return (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex">
                              <IconComponent size={24} className="mr-2" /> {opt.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </>
              )}
            />
            <form.AppField
              name="title_attribute_ref"
              children={(field) => (
                <>
                  <div className="flex items-center justify-between">
                    <Label className="uppercase text-xs font-bold text-muted-foreground">Entity Title:</Label>
                  </div>
                  <Select value={field.state.value} onValueChange={(e) => field.handleChange(e)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select Title Field" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default_id">Default (Entity)</SelectItem>
                      {form.state.values.attributes
                        .filter((attr) => attr.type === "Text" || attr.type === "Record")
                        .map((attr) => (
                          <SelectItem key={attr.name} value={attr.id || attr.name || attr.table_reference || ""}>
                            {attr.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[0.8rem] text-muted-foreground">
                    Select which text attribute should be used as the display title for this entity.
                  </p>
                </>
              )}
            />
          </CardContent>
        </Card>

        <Card className="max-w-2xl mx-auto mt-4">
          <CardHeader>
            <CardTitle>Attributes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {canCreateAttributes ? (
              <div className="text-right">
                <Button
                  type="button"
                  onClick={() => {
                    setAttrIndex(-1);
                    setTempAttribute(attr);
                    setAttributeDialogOpen(true);
                  }}
                >
                  <PlusIcon /> Add Attribute
                </Button>
              </div>
            ) : isSessionLoading ? (
              <Alert>
                <AlertDescription>Loading session information...</AlertDescription>
              </Alert>
            ) : (
              <Alert>
                <AlertDescription>Only administrators can create custom entity attributes.</AlertDescription>
              </Alert>
            )}
            <form.AppField
              name="attributes"
              children={(field) => (
                <>
                  <Dialog open={attributeDialogOpen} onOpenChange={setAttributeDialogOpen}>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{attrIndex === -1 ? "Add" : "Update"} Attribute</DialogTitle>
                        <DialogDescription>
                          <div className="w-full mb-2">
                            <p className="text-sm mb-2">Name:</p>
                            <Input
                              value={tempAttribute?.name}
                              onChange={(e) => {
                                setTempAttribute({ ...tempAttribute, name: e.target.value });
                              }}
                            />
                          </div>

                          <div className="w-full mb-2">
                            <div className="flex items-center gap-1 mb-2">
                              <p className="text-sm">Attribute ID:</p>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <AlertCircleIcon size={14} className="text-muted-foreground cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>The attribute ID cannot be edited after creation</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                            <Input
                              value={tempAttribute?.id}
                              disabled={attrIndex !== -1}
                              placeholder={tempAttribute.name ? makeAttributeId(tempAttribute.name) : "e.g. first_name"}
                              onChange={(e) => {
                                setTempAttribute({ ...tempAttribute, id: e.target.value });
                              }}
                            />
                            <p className="text-[10px] text-muted-foreground mt-1">
                              {attrIndex === -1 &&
                                "If kept empty, the ID will be automatically generated from the name."}{" "}
                              The attribute id is an immutable reference that can be used in automations and by ai
                              agents to perform tasks on the records.
                            </p>
                          </div>

                          <div className="w-full mb-2">
                            <p className="text-sm mb-2">Description:</p>
                            <Textarea
                              value={tempAttribute?.description}
                              onChange={(e) => {
                                setTempAttribute({ ...tempAttribute, description: e.target.value });
                              }}
                            />
                          </div>
                          {(!entity || attrIndex === -1) && (
                            <div className="w-full mb-2">
                              <p className="text-sm mb-2">Type:</p>
                              <Select
                                value={tempAttribute?.type}
                                onValueChange={(e) => {
                                  if (e === "Record") {
                                    setTempAttribute({
                                      ...tempAttribute,
                                      type: e,
                                      searchable: false,
                                      table_reference: "",
                                      defaultValue: "",
                                      richText: false,
                                    });
                                    return;
                                  }
                                  if (e === "TimeStamp" || e === "Date") {
                                    setTempAttribute({
                                      ...tempAttribute,
                                      type: e,
                                      searchable: false,
                                      dateTimeFormat: "default",
                                    });
                                    return;
                                  }
                                  if (optionsList.includes(e)) {
                                    setTempAttribute({
                                      ...tempAttribute,
                                      type: e as (typeof entityTypes)[number],
                                      options: [],
                                      defaultValue: [],
                                      richText: false,
                                    });
                                    return;
                                  }
                                  setTempAttribute({
                                    ...tempAttribute,
                                    type: e as (typeof entityTypes)[number],
                                    searchable: isAttributeTypeSearchable(e) ? tempAttribute.searchable : false,
                                    richText: false,
                                  });
                                }}
                              >
                                <SelectTrigger className="w-full mb-2">
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {entityTypes.map((item) => (
                                    <SelectItem key={item} value={item}>
                                      {item}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {tempAttribute.type === "Text" && (
                            <div className="flex items-center gap-2 mb-4">
                              <Switch
                                id="rich-text"
                                checked={!!tempAttribute.richText}
                                onCheckedChange={(checked) => {
                                  setTempAttribute({ ...tempAttribute, richText: checked, defaultValue: "" });
                                }}
                              />
                              <Label htmlFor="rich-text">Rich text</Label>
                            </div>
                          )}

                          {optionsList.includes(tempAttribute.type) && (
                            <div>
                              <div className="w-full mb-2">
                                <p className="text-sm mb-2">Options:</p>
                                <div className="flex items-center cursor-pointer relative">
                                  <Input
                                    value={tempOption}
                                    onChange={(e) => {
                                      setTempOption(e.target.value);
                                    }}
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter") {
                                        addOption();
                                      }
                                    }}
                                  />
                                  <PlusIcon onClick={addOption} className="absolute right-1 cursor-pointer" />
                                </div>
                              </div>
                              {tempAttribute?.options?.map((item) => (
                                <div key={item.value} className="flex w-full border p-2 items-center mt-4 rounded-md">
                                  <p className="w-full mb-2">{item.label}</p>
                                  <Trash2
                                    className="h-4 w-4 stroke-destructive cursor-pointer"
                                    onClick={() => {
                                      setTempAttribute((prev) => {
                                        return {
                                          ...prev,
                                          defaultValue: item.value === prev.defaultValue ? "" : prev.defaultValue,
                                          options: prev.options?.filter((opt) => opt !== item),
                                        };
                                      });
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          )}

                          {["Date", "TimeStamp"].includes(tempAttribute.type) ? (
                            <fieldset className="w-full mb-2 flex flex-col gap-2">
                              <label className="text-sm" htmlFor="dateTimeFormat">
                                Display as:
                              </label>
                              <Select
                                value={tempAttribute?.dateTimeFormat}
                                onValueChange={(e) => {
                                  setTempAttribute({
                                    ...tempAttribute,
                                    dateTimeFormat: e as (typeof tempAttribute)["dateTimeFormat"],
                                  });
                                }}
                              >
                                <SelectTrigger id="dateTimeFormat">
                                  <SelectValue placeholder="Select one" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="default">
                                    Default{" "}
                                    <em className="text-xs text-muted-foreground">
                                      (example: 2026-08-25
                                      {`${tempAttribute?.type === "TimeStamp" ? ", 00:00" : ""}`})
                                    </em>
                                  </SelectItem>
                                  <SelectItem value="relative">
                                    Relative Difference{" "}
                                    <em className="text-xs text-muted-foreground">
                                      (example: In 5 days, 2 Days ago, Yesterday)
                                    </em>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </fieldset>
                          ) : null}

                          {tempAttribute.type === "Record" && (
                            <div className="flex items-center">
                              <div className="w-full mb-2">
                                <p className="text-sm mb-2">Record:</p>
                                <Select
                                  key={`record-select-${tempAttribute?.table_reference}-${allAllowedTables.length}`}
                                  value={tempAttribute?.table_reference}
                                  onValueChange={(e) => {
                                    setTempAttribute({ ...tempAttribute, table_reference: e });
                                  }}
                                >
                                  <SelectTrigger className="w-full mb-2">
                                    <SelectValue placeholder="Select table" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {allAllowedTables.map((item) => (
                                      <SelectItem key={item.value} value={item.value}>
                                        {item.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          )}

                          {tempAttribute.type === "Multi Record" && (
                            <div className="w-full mb-2">
                              <div className="flex items-center gap-2 mb-2">
                                <p className="text-sm ">Multi Record Type:</p>
                              </div>
                              <FancyMultiSelect
                                key={`multi-record-select-${tempAttribute?.table_reference}-${allAllowedTables.length}`}
                                items={allAllowedTables.map((item) => ({
                                  value: item.value,
                                  label: item.label,
                                }))}
                                selectedItems={
                                  tempAttribute.table_reference
                                    ? tempAttribute.table_reference.includes(",")
                                      ? tempAttribute.table_reference.split(",").map((o) => ({
                                          value: o,
                                          label: allAllowedTables.find((t) => o === t.value)?.label || o,
                                        }))
                                      : [
                                          {
                                            value: tempAttribute.table_reference,
                                            label:
                                              allAllowedTables.find((o) => o.value === tempAttribute.table_reference)
                                                ?.label || tempAttribute.table_reference,
                                          },
                                        ]
                                    : []
                                }
                                onValueChange={(value: { value: string; label: string }[]) => {
                                  setTempAttribute({
                                    ...tempAttribute,
                                    table_reference: value.map((i) => i.value).join(","),
                                  });
                                }}
                              />
                            </div>
                          )}
                          {isAttributeTypeSearchable(tempAttribute.type) && (
                            <div className="w-full mb-2">
                              <p className="text-sm mb-2">Searchable:</p>
                              <Switch
                                checked={!!tempAttribute.searchable}
                                onCheckedChange={(checked) => {
                                  setTempAttribute({ ...tempAttribute, searchable: checked });
                                }}
                              />
                            </div>
                          )}

                          {tempAttribute.type && tempAttribute.type !== "Record" && (
                            <div className="w-full mb-2">
                              <p className="text-sm mb-2">Default Value:</p>
                              {["Text", "Number"].includes(tempAttribute.type) &&
                                (tempAttribute.type === "Text" && tempAttribute.richText ? (
                                  <TextEditor
                                    content={
                                      typeof tempAttribute?.defaultValue === "string" ? tempAttribute?.defaultValue : ""
                                    }
                                    onChange={(content) => {
                                      setTempAttribute({ ...tempAttribute, defaultValue: content });
                                    }}
                                  />
                                ) : (
                                  <Input
                                    type={tempAttribute.type === "Number" ? "number" : "text"}
                                    value={
                                      typeof tempAttribute?.defaultValue === "string" ? tempAttribute?.defaultValue : ""
                                    }
                                    onChange={(e) => {
                                      setTempAttribute({ ...tempAttribute, defaultValue: e.target.value });
                                    }}
                                  />
                                ))}
                              {["Checkbox"].includes(tempAttribute.type) && (
                                <Switch
                                  checked={
                                    typeof tempAttribute?.defaultValue === "boolean"
                                      ? tempAttribute?.defaultValue
                                      : false
                                  }
                                  onCheckedChange={(checked) => {
                                    setTempAttribute({ ...tempAttribute, defaultValue: checked });
                                  }}
                                />
                              )}
                              {["Date", "TimeStamp"].includes(tempAttribute.type) && (
                                <Select
                                  value={
                                    typeof tempAttribute?.defaultValue === "string" ? tempAttribute?.defaultValue : ""
                                  }
                                  onValueChange={(e) => {
                                    setTempAttribute({ ...tempAttribute, defaultValue: e });
                                  }}
                                >
                                  <SelectTrigger className="w-full mb-2">
                                    <SelectValue placeholder="Select Option" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {dateOptions.map((item) => (
                                      <SelectItem key={item.value} value={item.value}>
                                        {item.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              {["Select"].includes(tempAttribute.type) && (
                                <Select
                                  value={
                                    typeof tempAttribute?.defaultValue === "string" ? tempAttribute?.defaultValue : ""
                                  }
                                  onValueChange={(e) => {
                                    setTempAttribute({ ...tempAttribute, defaultValue: e });
                                  }}
                                >
                                  <SelectTrigger className="w-full mb-2">
                                    <SelectValue placeholder="Select Option" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {tempAttribute.options?.map((item) => (
                                      <SelectItem key={item.value} value={item.value}>
                                        {item.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              {["Multi Select"].includes(tempAttribute.type) && (
                                <>
                                  <FancyMultiSelect
                                    items={tempAttribute.options || []}
                                    selectedItems={
                                      Array.isArray(tempAttribute.defaultValue) ? tempAttribute.defaultValue : []
                                    }
                                    onValueChange={(e: { label: string; value: string }[]) => {
                                      if (Array.isArray(tempAttribute.defaultValue)) {
                                        setTempAttribute({
                                          ...tempAttribute,
                                          defaultValue: e,
                                        });
                                      }
                                    }}
                                  />
                                  <p className="text-sm mb-2">Creatable:</p>
                                  <Switch
                                    checked={tempAttribute.optionsCreatable}
                                    onCheckedChange={(checked) => {
                                      setTempAttribute({
                                        ...tempAttribute,
                                        optionsCreatable: checked,
                                      });
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          )}
                          <div className="w-full mb-2">
                            <p className="text-sm mb-2">Boundaries:</p>
                            <p className="text-sm mb-2">Required:</p>
                            <Switch
                              checked={tempAttribute.boundaries.required}
                              onCheckedChange={(checked) => {
                                setTempAttribute({
                                  ...tempAttribute,
                                  boundaries: { ...tempAttribute.boundaries, required: checked },
                                });
                              }}
                            />
                            <p className="text-sm mb-2">Unique:</p>
                            <Switch
                              checked={tempAttribute.boundaries.unique}
                              onCheckedChange={(checked) => {
                                setTempAttribute({
                                  ...tempAttribute,
                                  boundaries: { ...tempAttribute.boundaries, unique: checked },
                                });
                              }}
                            />
                          </div>
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter>
                        {attrIndex !== -1 && (
                          <Button
                            className="mr-2"
                            variant={"outline"}
                            type="button"
                            onClick={() => {
                              setAttrIndex(-1);
                              setTempAttribute(attr);
                              setAttributeDialogOpen(false);
                            }}
                          >
                            Cancel
                          </Button>
                        )}
                        <Button
                          type="button"
                          onClick={() => {
                            addAttribute(field);
                          }}
                        >
                          {attrIndex === -1 ? "Add" : "Update"} Attribute
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                  <AttributesList
                    attributes={field.state.value}
                    onReorder={(newAttributes) => field.setValue(newAttributes)}
                    onEdit={(index, attribute) => {
                      setAttrIndex(index);
                      setTempAttribute({ ...attribute, searchable: attribute.searchable ?? false });
                      setAttributeDialogOpen(true);
                    }}
                    onDelete={(index) => {
                      const attributeToDelete = field.state.value[index];
                      if (!attributeToDelete) return;
                      const warnings: string[] = [];

                      if (customViews && customViews.length > 0) {
                        customViews.forEach((view) => {
                          if (view.field_ref === attributeToDelete.id) {
                            warnings.push(
                              `This field organizes the "${view.name}" view. Remove it from the view before deleting.`,
                            );
                          }
                          if ((view.card_fields || []).includes(attributeToDelete.id)) {
                            warnings.push(
                              `This field is displayed on cards in the "${view.name}" view. Remove it from the view before deleting.`,
                            );
                          }
                        });
                      }

                      if (warnings.length > 0) {
                        setDeleteWarningDialog({ open: true, index, warnings });
                      } else {
                        if (attrIndex !== -1) {
                          setAttrIndex(-1);
                        }
                        const tempA = [...field.state.value].filter((_, i) => i !== index);
                        field.setValue(tempA);
                      }
                    }}
                  />
                  <Dialog
                    open={deleteWarningDialog.open}
                    onOpenChange={(open) => setDeleteWarningDialog((prev) => ({ ...prev, open }))}
                  >
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <AlertTriangleIcon className="h-5 w-5 text-amber-500" />
                          Warning: Field is in use
                        </DialogTitle>
                        <DialogDescription className="space-y-2">
                          {deleteWarningDialog.warnings.map((warning, i) => (
                            <p key={i}>{warning}</p>
                          ))}
                          <p className="pt-2">Are you sure you want to delete this field?</p>
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter>
                        <Button
                          variant="outline"
                          onClick={() => setDeleteWarningDialog({ open: false, index: -1, warnings: [] })}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => {
                            if (attrIndex !== -1) {
                              setAttrIndex(-1);
                            }
                            const tempA = [...field.state.value].filter((_, i) => i !== deleteWarningDialog.index);
                            field.setValue(tempA);
                            setDeleteWarningDialog({ open: false, index: -1, warnings: [] });
                          }}
                        >
                          Delete Anyway
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </>
              )}
            />
          </CardContent>
        </Card>
        <Card className="p-4 max-w-2xl mx-auto mt-4">
          <small>
            By default, only <strong>Administrator</strong>{" "}
            {session?.user?.role !== "Administrator" && (
              <>
                and <strong>{session?.user?.role}</strong>
              </>
            )}{" "}
            have access to this custom entity. To grant access to other users or roles, please update the settings in
            the <strong>Permissions</strong> area.
          </small>
        </Card>
        <div className="max-w-2xl mx-auto mt-4">
          <div className="flex w-full justify-end">
            <form.AppForm>
              <Button type="submit" disabled={entity ? editData.isPending : createData.isPending}>
                {btnTitle()}
              </Button>
            </form.AppForm>
          </div>

          {(createData.isSuccess || editData.isSuccess) && (
            <div className="flex items-center text-sm font-medium text-green-600">
              <CheckCircle2Icon className="h-4 w-4 mr-1" />
              Form submitted successfully
            </div>
          )}

          {(createData.isError || editData.isError) && (
            <div className="flex items-center text-sm font-medium text-destructive">
              <AlertCircleIcon className="h-4 w-4 mr-1" />
              Form submission failed. Please try again.
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

type AttributeType = z.input<typeof dataSchema>["attributes"][number];

function AttributesList({
  attributes,
  onReorder,
  onEdit,
  onDelete,
}: {
  attributes: AttributeType[];
  onReorder: (attributes: AttributeType[]) => void;
  onEdit: (index: number, attribute: AttributeType) => void;
  onDelete: (index: number) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = attributes.findIndex((attr) => attr.id === active.id);
      const newIndex = attributes.findIndex((attr) => attr.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(arrayMove(attributes, oldIndex, newIndex));
      }
    }
  };

  const attributeIds = attributes.map((attr) => attr.id);

  if (attributes.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground border rounded-md">
        No attributes yet. Click "Add Attribute" to get started.
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext items={attributeIds} strategy={verticalListSortingStrategy}>
        <div className="border rounded-md">
          <div className="grid grid-cols-[auto_1fr_1fr_1fr_auto] gap-2 p-3 bg-muted/50 border-b font-medium text-sm">
            <div className="w-6" />
            <div>Name</div>
            <div>ID</div>
            <div>Type</div>
            <div>Actions</div>
          </div>
          <div className="divide-y">
            {attributes.map((attribute, index) => (
              <SortableAttributeRow
                key={attribute.id}
                attribute={attribute}
                index={index}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </div>
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableAttributeRow({
  attribute,
  index,
  onEdit,
  onDelete,
}: {
  attribute: AttributeType;
  index: number;
  onEdit: (index: number, attribute: AttributeType) => void;
  onDelete: (index: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: attribute.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "grid grid-cols-[auto_1fr_1fr_1fr_auto] gap-2 p-3 items-center bg-background",
        isDragging && "shadow-lg",
      )}
    >
      <div className="cursor-grab active:cursor-grabbing" {...listeners} {...attributes}>
        <GripVerticalIcon className="h-5 w-5 text-muted-foreground" />
      </div>
      <div className="truncate">{attribute.name}</div>
      <div className="truncate">
        <code>{attribute.id?.trim() || makeAttributeId(attribute.name)}</code>
      </div>
      <div className="truncate text-muted-foreground">
        {attribute.type}
        {attribute.richText && " (Rich)"}
      </div>
      <div className="flex items-center gap-2">
        <EditIcon onClick={() => onEdit(index, attribute)} size={18} className="cursor-pointer text-green-500" />
        <Trash2 onClick={() => onDelete(index)} size={18} className="cursor-pointer stroke-destructive" />
      </div>
    </div>
  );
}
